import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, MessageSquare, Plug, CheckCircle2, Plus, Trash2, Send, BarChart3, Sparkles, X,
  ShieldAlert, Info, Check, Wallet, Gauge, Settings, DollarSign,
} from "lucide-react";
import { sr, auth } from "../../lib/sr";

const money = (v) => `$${Number(v || 0).toLocaleString()}`;
const STAFF = ["admin", "super_admin", "campaign_manager", "compliance_reviewer", "salesperson"];

const STATUS_META = {
  draft: ["Draft", "sr-badge-gray"],
  ready: ["Ready to submit", "sr-badge-green"],
  awaiting_provider: ["Awaiting activation", "sr-badge-amber"],
  submitting: ["Submitting", "sr-badge-amber"],
  under_review: ["Under review", "sr-badge-amber"],
  scheduled: ["Scheduled", "sr-badge-green"],
  running: ["Running", "sr-badge-green"],
  paused: ["Paused", "sr-badge-gray"],
  rejected: ["Rejected", "sr-badge-amber"],
  completed: ["Completed", "sr-badge-gray"],
  needs_attention: ["Needs attention", "sr-badge-amber"],
};

function CharInput({ label, value, onChange, max, testid, textarea, placeholder }) {
  const len = (value || "").length;
  const over = len > max;
  const Comp = textarea ? "textarea" : "input";
  return (
    <div className="sr-field">
      <label className="sr-label" style={{ display: "flex", justifyContent: "space-between" }}>
        <span>{label}</span>
        <span style={{ color: over ? "#B42318" : "var(--muted)", fontWeight: 600, fontSize: 11 }} data-testid={`${testid}-count`}>{len}/{max}</span>
      </label>
      <Comp className="sr-input" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        rows={textarea ? 2 : undefined} data-testid={testid} style={over ? { borderColor: "#B42318" } : undefined} />
    </div>
  );
}

// Compact illustrative sponsored-card preview — final rendering is controlled by the publisher.
function AdPreview({ biz, creative, testid }) {
  const c = creative || {};
  return (
    <div data-testid={testid} style={{ border: "1px solid var(--border)", borderRadius: 12, overflow: "hidden", background: "#fff", maxWidth: 340 }}>
      <div style={{ padding: "6px 10px", fontSize: 10.5, color: "var(--muted)", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", gap: 6, background: "#F8FAFC" }}>
        <Info size={11} /> Illustrative preview · Sponsored
      </div>
      <div style={{ display: "flex", gap: 10, padding: 10 }}>
        <div style={{ width: 56, height: 56, borderRadius: 8, flexShrink: 0, background: c.image_url ? `center/cover no-repeat url(${c.image_url})` : "#EEF2F7", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--muted)" }}>
          {!c.image_url && <MessageSquare size={18} />}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, color: "var(--muted)" }}>{biz || "Your business"}</div>
          <div style={{ fontSize: 14, color: "var(--navy)", fontWeight: 700, lineHeight: 1.2 }}>{c.title || "Your headline"}</div>
          <div style={{ fontSize: 12, color: "var(--navy)", marginTop: 2 }}>{c.body || "Your short description shows here."}</div>
        </div>
      </div>
    </div>
  );
}

export default function ChatGptAds() {
  const me = auth.getUser();
  const isStaff = !!(me && STAFF.includes(me.role));
  const [status, setStatus] = useState(null);
  const [campaigns, setCampaigns] = useState(null);
  const [advertisers, setAdvertisers] = useState([]);
  const [learn, setLearn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actId, setActId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [report, setReport] = useState({});
  const [aiPlan, setAiPlan] = useState({});
  const [markup, setMarkup] = useState("");
  const [cr, setCr] = useState({}); // per-campaign creative draft {title, body, image_url}
  const [form, setForm] = useState({
    name: "", business_name: "", goal: "awareness", budget: "", start: "", end: "",
    geo_country: "US", themes: "", offer_headline: "", offer_detail: "", destination_url: "", advertiser_id: "",
  });
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const load = () => Promise.all([sr.chatgptAds.status(), sr.chatgptAds.campaigns(), sr.chatgptAds.advertisers().catch(() => ({ advertisers: [] }))])
    .then(([s, c, a]) => {
      setStatus(s); setCampaigns(c.campaigns || []); setAdvertisers(a.advertisers || []);
      if (s.markup_pct != null) setMarkup(String(s.markup_pct));
      setForm((p) => ({ ...p, advertiser_id: p.advertiser_id || s.default_advertiser_id || "" }));
    })
    .catch(() => { setStatus({ connected: false }); setCampaigns([]); });
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const savePricing = async () => {
    setBusy(true);
    try { await sr.chatgptAds.setConfig({ markup_pct: parseFloat(markup) }); toast.success("ChatGPT Ads pricing saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save pricing"); }
    finally { setBusy(false); }
  };

  const create = async () => {
    if (!form.name.trim()) return toast.error("Name the campaign");
    if (!(parseFloat(form.budget) > 0)) return toast.error("Enter a budget");
    if (!form.start || !form.end) return toast.error("Pick flight start and end dates");
    setBusy(true);
    try {
      await sr.chatgptAds.create({
        name: form.name.trim(), business_name: form.business_name.trim(), goal: form.goal,
        budget: parseFloat(form.budget), start: form.start, end: form.end,
        geo: { country: form.geo_country },
        themes: form.themes.split(",").map((t) => t.trim()).filter(Boolean),
        offer: { headline: form.offer_headline.trim(), detail: form.offer_detail.trim() },
        destination_url: form.destination_url.trim() || undefined,
        advertiser_id: form.advertiser_id || undefined,
      });
      toast.success("ChatGPT Ads campaign created");
      setForm((p) => ({ ...p, name: "", budget: "", themes: "", offer_headline: "", offer_detail: "" }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create the campaign"); }
    finally { setBusy(false); }
  };

  const act = async (id, fn, okMsg) => {
    setActId(id);
    try { const r = await fn(); if (okMsg) toast.success(typeof okMsg === "function" ? okMsg(r) : okMsg); load(); return r; }
    catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message || "Action failed" : (d || "Action failed"));
    } finally { setActId(null); }
  };

  const saveCreative = async (id) => {
    const d = cr[id] || {};
    setActId(id);
    try {
      const r = await sr.chatgptAds.setCreative(id, { title: d.title || "", body: d.body || "", image_url: d.image_url || undefined });
      (r.validation?.errors || []).forEach((m) => toast.error(m));
      (r.validation?.warnings || []).forEach((m) => toast.message(m));
      if (r.validation?.valid) toast.success("Creative saved");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save creative"); }
    finally { setActId(null); }
  };

  const runAiPlan = async (c) => {
    setActId(`ai-${c.id}`);
    try {
      const r = await sr.chatgptAds.aiPlan({ campaign_id: c.id });
      setAiPlan((p) => ({ ...p, [c.id]: r.plan }));
      if (!r.ai_available) toast.message("AI was busy — showing a starter plan you can edit.");
    } catch (e) { toast.error("Could not generate a plan"); }
    finally { setActId(null); }
  };

  const applyConcept = (id, concept) => {
    setCr((p) => ({ ...p, [id]: { ...(p[id] || {}), title: concept.headline || "", body: concept.body || "" } }));
    setExpanded(id);
    toast.success("Concept loaded into the creative builder — edit and save");
  };

  const getReport = (id) => act(id, async () => { const r = await sr.chatgptAds.report(id); setReport((p) => ({ ...p, [id]: r })); return r; });

  if (!campaigns) return <div className="sr-empty" style={{ padding: 60, textAlign: "center" }}><Loader2 className="sr-spin" /></div>;

  const pricingOn = status?.pricing_configured;

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "8px 4px 40px" }} data-testid="chatgpt-page">
      <h1 style={{ color: "var(--navy)", fontSize: 26, margin: 0, display: "inline-flex", alignItems: "center", gap: 8 }}>
        <MessageSquare size={22} /> ChatGPT Ads
      </h1>
      <p className="sr-muted" style={{ marginTop: 6 }}>
        Reach people researching products and services in ChatGPT. Relevant conversations. Concise creative. Measurable results.
        <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginLeft: 8 }} onClick={() => setLearn((v) => !v)} data-testid="chatgpt-learn-toggle"><Info size={13} /> Learn how it works</button>
      </p>
      {learn && (
        <div className="sr-card" style={{ marginTop: 10, background: "#F8FAFC" }} data-testid="chatgpt-learn-panel">
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "var(--navy)", lineHeight: 1.7 }}>
            <li>Sponsored ads may appear alongside relevant ChatGPT conversations.</li>
            <li>Each ad shows your brand, an image, a headline, a short description and a destination link.</li>
            <li>Your topics and creative help the system understand relevance.</li>
            <li>Placement and delivery remain subject to eligibility, review and relevance.</li>
          </ul>
          <p className="sr-muted" style={{ fontSize: 12, marginTop: 8 }}>SmartReachAI plans, buys and manages this channel for you — no separate ad accounts or tokens.</p>
        </div>
      )}

      {/* Capability banners */}
      {status?.connected ? (
        <div className="sr-card" style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8 }} data-testid="chatgpt-connected">
          <CheckCircle2 size={16} style={{ color: "#0E7A55" }} />
          <span style={{ color: "var(--navy)", fontWeight: 600 }}>ChatGPT Ads channel connected</span>
          {status?.readable && <span className="sr-badge sr-badge-green" style={{ fontSize: 11 }}>Reporting live</span>}
          <span className="sr-muted" style={{ fontSize: 12 }}>· {advertisers.length} advertiser{advertisers.length === 1 ? "" : "s"}</span>
        </div>
      ) : (
        <div className="sr-card" style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 8, background: "#FFF7ED", border: "1px solid #FED7AA" }} data-testid="chatgpt-disconnected">
          <Plug size={16} style={{ color: "#B45309" }} />
          <span style={{ color: "#92400E", fontSize: 13.5 }}>The ChatGPT Ads channel isn't connected yet. You can still draft campaigns below.</span>
        </div>
      )}
      {status?.connected && !status?.writable && (
        <div className="sr-card" style={{ marginTop: 8, display: "flex", alignItems: "flex-start", gap: 8, background: "#FFFBEB", border: "1px solid #FDE68A" }} data-testid="chatgpt-write-gated">
          <ShieldAlert size={15} style={{ color: "#B45309", marginTop: 1 }} />
          <span style={{ color: "#92400E", fontSize: 13 }}>
            Live buying isn't available yet — the ad platform's ChatGPT Ads API is read-only on this account.
            Plan, approve and fund now; campaigns activate automatically the moment the write API ships. No spend happens until then.
          </span>
        </div>
      )}
      {!pricingOn && (
        <div className="sr-card" style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, background: "#F8FAFC", border: "1px solid var(--border)" }} data-testid="chatgpt-pricing-missing">
          <DollarSign size={15} style={{ color: "#64748B" }} />
          <span className="sr-muted" style={{ fontSize: 13 }}>ChatGPT Ads pricing isn't configured yet{isStaff ? " — set it below to enable checkout." : ". An admin sets pricing before this channel can be purchased."}</span>
        </div>
      )}
      {status?.connected && status?.test_mode && (
        <div className="sr-card" style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, background: "#F8FAFC", border: "1px solid var(--border)" }} data-testid="chatgpt-testmode">
          <ShieldAlert size={15} style={{ color: "#64748B" }} />
          <span className="sr-muted" style={{ fontSize: 13 }}>Test Mode is ON — an admin switches to Production Mode to activate campaigns.</span>
        </div>
      )}

      {/* Admin pricing config */}
      {isStaff && (
        <div className="sr-card" style={{ marginTop: 14 }} data-testid="chatgpt-pricing-config">
          <strong style={{ color: "var(--navy)", display: "inline-flex", alignItems: "center", gap: 6 }}><Settings size={15} /> ChatGPT Ads pricing (admin)</strong>
          <p className="sr-muted" style={{ fontSize: 12.5, marginTop: 6 }}>Markup on provider media cost. Checkout stays blocked until this is set. Suggested: {status?.suggested_markup_pct ?? 55}%.</p>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
            <input className="sr-input" type="number" min="0" step="1" value={markup} onChange={(e) => setMarkup(e.target.value)} placeholder="55" style={{ width: 120 }} data-testid="chatgpt-markup-input" />
            <span className="sr-muted" style={{ fontSize: 13 }}>% markup</span>
            <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={savePricing} disabled={busy || markup === ""} data-testid="chatgpt-save-pricing">{busy ? <Loader2 size={13} className="sr-spin" /> : "Save pricing"}</button>
            {status?.markup_pct != null && <span className="sr-badge sr-badge-green" style={{ fontSize: 11 }}>Active · {status.markup_pct}%</span>}
          </div>
        </div>
      )}

      {/* New campaign */}
      <div className="sr-card" style={{ marginTop: 14 }} data-testid="chatgpt-new">
        <strong style={{ color: "var(--navy)" }}>New ChatGPT Ads campaign</strong>
        <div className="sr-field" style={{ marginTop: 10 }}><label className="sr-label">Campaign name</label>
          <input className="sr-input" value={form.name} onChange={set("name")} placeholder="e.g. Weekday Dinners — ChatGPT" data-testid="chatgpt-name" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="sr-field"><label className="sr-label">Business</label>
            <input className="sr-input" value={form.business_name} onChange={set("business_name")} placeholder="Your business name" data-testid="chatgpt-business" /></div>
          <div className="sr-field"><label className="sr-label">Goal</label>
            <select className="sr-input" value={form.goal} onChange={set("goal")} data-testid="chatgpt-goal">
              {(status?.goals || []).map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
            </select></div>
        </div>
        {(status?.goals || []).find((g) => g.key === form.goal) && (
          <p className="sr-muted" style={{ fontSize: 11.5, marginTop: -4, marginBottom: 8 }} data-testid="chatgpt-goal-note">{(status.goals.find((g) => g.key === form.goal) || {}).note}</p>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div className="sr-field"><label className="sr-label">Budget ($, all-in)</label>
            <input className="sr-input" type="number" min="0" value={form.budget} onChange={set("budget")} placeholder="1500" data-testid="chatgpt-budget" /></div>
          <div className="sr-field"><label className="sr-label">Flight start</label>
            <input className="sr-input" type="date" value={form.start} onChange={set("start")} data-testid="chatgpt-start" /></div>
          <div className="sr-field"><label className="sr-label">Flight end</label>
            <input className="sr-input" type="date" value={form.end} onChange={set("end")} data-testid="chatgpt-end" /></div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="sr-field"><label className="sr-label">Country</label>
            <select className="sr-input" value={form.geo_country} onChange={set("geo_country")} data-testid="chatgpt-geo-country">
              {(status?.geo_options?.countries || [{ code: "US", name: "United States" }]).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select></div>
          {status?.connected && isStaff && (
            <div className="sr-field"><label className="sr-label">Ad platform advertiser</label>
              <select className="sr-input" value={form.advertiser_id} onChange={set("advertiser_id")} data-testid="chatgpt-advertiser">
                <option value="">Select…</option>
                {advertisers.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select></div>
          )}
        </div>
        {status?.geo_options?.note && <p className="sr-muted" style={{ fontSize: 11, marginTop: -4 }}>{status.geo_options.note}</p>}
        <div className="sr-field"><label className="sr-label">Conversation themes <span className="sr-muted" style={{ fontWeight: 400 }}>· comma-separated — topics your customers may be exploring</span></label>
          <input className="sr-input" value={form.themes} onChange={set("themes")} placeholder="planning a family dinner, best brunch nearby" data-testid="chatgpt-themes" /></div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div className="sr-field"><label className="sr-label">Offer headline</label>
            <input className="sr-input" value={form.offer_headline} onChange={set("offer_headline")} placeholder="15% off weekday dinners" data-testid="chatgpt-offer-headline" /></div>
          <div className="sr-field"><label className="sr-label">Offer detail</label>
            <input className="sr-input" value={form.offer_detail} onChange={set("offer_detail")} placeholder="Dine in Mon–Thu" data-testid="chatgpt-offer-detail" /></div>
        </div>
        <div className="sr-field"><label className="sr-label">Destination URL</label>
          <input className="sr-input" value={form.destination_url} onChange={set("destination_url")} placeholder="https://…" data-testid="chatgpt-destination" /></div>
        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={create} disabled={busy} data-testid="chatgpt-create">
          {busy ? <Loader2 size={13} className="sr-spin" /> : <><Plus size={14} /> Create campaign</>}
        </button>
      </div>

      {/* Campaign list */}
      <h2 style={{ color: "var(--navy)", fontSize: 15, margin: "20px 0 8px" }}>Your ChatGPT Ads campaigns</h2>
      {campaigns.length === 0 ? (
        <div className="sr-card sr-empty" style={{ textAlign: "center", padding: 30 }} data-testid="chatgpt-empty">
          <MessageSquare size={24} style={{ color: "var(--muted)" }} /><p style={{ marginTop: 8 }}>No ChatGPT Ads campaigns yet.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {campaigns.map((c) => {
            const [slabel, scls] = STATUS_META[c.customer_status] || STATUS_META.draft;
            const rd = c.readiness || { ready: false, missing: [] };
            const d = cr[c.id] || c.creative || {};
            const rep = report[c.id];
            const plan = aiPlan[c.id];
            return (
              <div key={c.id} className="sr-card" data-testid={`chatgpt-campaign-${c.id}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <strong style={{ color: "var(--navy)" }}>{c.name}</strong>
                  <span className={`sr-badge ${scls}`} data-testid={`chatgpt-status-${c.id}`}>{slabel}</span>
                  {c.quote_customer_total != null && <span className="sr-badge sr-badge-gray"><Wallet size={11} /> {money(c.quote_customer_total)}</span>}
                  {c.funds_reserved && <span className="sr-badge sr-badge-green" data-testid={`chatgpt-reserved-${c.id}`}>Funds reserved</span>}
                  <span className="sr-muted" style={{ fontSize: 12.5, marginLeft: "auto" }}>{money(c.budget)} · {c.start || "—"} → {c.end || "—"}</span>
                </div>
                <div className="sr-muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                  {c.business_name || "Business"} · {(status?.goals || []).find((g) => g.key === c.goal)?.label || c.goal} · {(c.geo || {}).country || "US"}
                  {(c.themes || []).length > 0 && <> · Themes: {c.themes.join(", ")}</>}
                </div>
                {!rd.ready && <div className="sr-muted" style={{ fontSize: 12, marginTop: 4 }} data-testid={`chatgpt-next-${c.id}`}>Next: {rd.next_action}</div>}
                {c.customer_status === "awaiting_provider" && (
                  <div style={{ fontSize: 12.5, marginTop: 6, color: "#92400E", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, padding: "6px 10px" }} data-testid={`chatgpt-gated-${c.id}`}>
                    Submitted to operations — activates automatically once the ad platform's ChatGPT write API is available. No spend yet.
                  </div>
                )}

                {/* Creative preview + builder */}
                <div style={{ display: "flex", gap: 16, marginTop: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
                  <AdPreview biz={c.business_name} creative={d} testid={`chatgpt-preview-${c.id}`} />
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setExpanded(expanded === c.id ? null : c.id)} data-testid={`chatgpt-build-${c.id}`}>
                      <Sparkles size={13} /> {expanded === c.id ? "Hide creative builder" : "Build / edit creative"}
                    </button>
                    <button className="sr-btn sr-btn-ghost sr-btn-sm" style={{ marginLeft: 8 }} onClick={() => runAiPlan(c)} disabled={actId === `ai-${c.id}`} data-testid={`chatgpt-aiplan-${c.id}`}>
                      {actId === `ai-${c.id}` ? <Loader2 size={13} className="sr-spin" /> : <><Sparkles size={13} /> Generate my ChatGPT ad plan</>}
                    </button>
                    {expanded === c.id && (
                      <div style={{ marginTop: 10, borderTop: "1px solid var(--border)", paddingTop: 10 }} data-testid={`chatgpt-creative-form-${c.id}`}>
                        <CharInput label="Headline" value={d.title || ""} max={status?.creative_rules?.title_max || 30} testid={`chatgpt-creative-title-${c.id}`}
                          onChange={(v) => setCr((p) => ({ ...p, [c.id]: { ...(p[c.id] || c.creative || {}), title: v } }))} placeholder="Short, benefit-led headline" />
                        <CharInput label="Description" value={d.body || ""} max={status?.creative_rules?.body_max || 60} testid={`chatgpt-creative-body-${c.id}`} textarea
                          onChange={(v) => setCr((p) => ({ ...p, [c.id]: { ...(p[c.id] || c.creative || {}), body: v } }))} placeholder="One concise line" />
                        <div className="sr-field"><label className="sr-label">Image URL <span className="sr-muted" style={{ fontWeight: 400 }}>· {status?.creative_rules?.image_hint || "256 × 256 square"}</span></label>
                          <input className="sr-input" value={d.image_url || ""} data-testid={`chatgpt-creative-image-${c.id}`}
                            onChange={(e) => setCr((p) => ({ ...p, [c.id]: { ...(p[c.id] || c.creative || {}), image_url: e.target.value } }))} placeholder="https://…/image-256.png" /></div>
                        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => saveCreative(c.id)} disabled={actId === c.id} data-testid={`chatgpt-creative-save-${c.id}`}>
                          {actId === c.id ? <Loader2 size={13} className="sr-spin" /> : "Save creative"}
                        </button>
                        {(c.creative_validation?.warnings || []).map((w, i) => <p key={i} className="sr-muted" style={{ fontSize: 11, marginTop: 6 }}>{w}</p>)}
                      </div>
                    )}
                    {plan && (
                      <div style={{ marginTop: 10, background: "#F8FAFC", borderRadius: 10, padding: 10 }} data-testid={`chatgpt-aiplan-result-${c.id}`}>
                        <div style={{ fontSize: 12.5, color: "var(--navy)" }}>{plan.strategy}</div>
                        {(plan.themes || []).length > 0 && <div className="sr-muted" style={{ fontSize: 11.5, marginTop: 6 }}>Themes: {plan.themes.join(" · ")}</div>}
                        <div style={{ display: "grid", gap: 6, marginTop: 8 }}>
                          {(plan.concepts || []).map((cc, i) => (
                            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", border: "1px solid var(--border)", borderRadius: 8, padding: "6px 8px", background: "#fff" }}>
                              <div style={{ minWidth: 0 }}><strong style={{ fontSize: 12.5, color: "var(--navy)" }}>{cc.headline}</strong><div className="sr-muted" style={{ fontSize: 11.5 }}>{cc.body}</div></div>
                              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => applyConcept(c.id, cc)} data-testid={`chatgpt-use-concept-${c.id}-${i}`}>Use</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Lifecycle actions */}
                <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                  <button className="sr-btn sr-btn-sm sr-btn-ghost" disabled={actId === c.id} onClick={() => act(c.id, () => sr.chatgptAds.quote(c.id), (r) => `Quote: ${money(r.quote.customer_total)} all-in`)} data-testid={`chatgpt-quote-${c.id}`}><DollarSign size={12} /> Get quote</button>
                  <button className="sr-btn sr-btn-sm sr-btn-ghost" disabled={actId === c.id} onClick={() => act(c.id, () => sr.chatgptAds.approve(c.id, { mode: "self_service" }), "Approved")} data-testid={`chatgpt-approve-${c.id}`}><Check size={12} /> Approve</button>
                  <button className="sr-btn sr-btn-sm sr-btn-ghost" disabled={actId === c.id} onClick={() => act(c.id, () => sr.chatgptAds.reserve(c.id), "Funds reserved")} data-testid={`chatgpt-reserve-${c.id}`}><Wallet size={12} /> Reserve funds</button>
                  {status?.can_submit ? (
                    <button className="sr-btn sr-btn-sm sr-btn-primary" disabled={actId === c.id} onClick={() => act(c.id, () => sr.chatgptAds.submit(c.id), (r) => r.activated ? "Activated" : "Submitted for activation")} data-testid={`chatgpt-submit-${c.id}`}>
                      {actId === c.id ? <Loader2 size={12} className="sr-spin" /> : <><Send size={12} /> Submit to go live</>}
                    </button>
                  ) : (
                    <span className="sr-muted" style={{ fontSize: 12, alignSelf: "center" }} data-testid={`chatgpt-await-admin-${c.id}`}>An admin takes this live</span>
                  )}
                  <button className="sr-btn sr-btn-sm sr-btn-ghost" disabled={actId === c.id} onClick={() => getReport(c.id)} data-testid={`chatgpt-report-${c.id}`}><BarChart3 size={12} /> Results</button>
                  <button className="sr-btn sr-btn-sm sr-btn-ghost" disabled={actId === c.id} onClick={() => act(c.id, () => sr.chatgptAds.remove(c.id), "Campaign removed")} data-testid={`chatgpt-delete-${c.id}`}><Trash2 size={12} /></button>
                </div>
                {rep && (
                  <div style={{ marginTop: 8, fontSize: 13, color: "var(--navy)" }} data-testid={`chatgpt-report-result-${c.id}`}>
                    {rep.available ? (
                      <><Gauge size={13} style={{ verticalAlign: "-2px" }} /> {Number(rep.impressions).toLocaleString()} impressions · {Number(rep.clicks).toLocaleString()} clicks · {money(rep.spend)} spend{rep.ctr != null ? ` · ${rep.ctr}% CTR` : ""}</>
                    ) : (
                      <span className="sr-muted">{rep.note}</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
